import type { Editor } from "grapesjs";
import { VARIABLE_GROUPS } from "../variables";

const COMMAND_ID = "tvs-open-variable-picker";
const PANEL_ID = "tvs-variable-panel";

const buildPanelHtml = (): string => {
  const groups = VARIABLE_GROUPS.map((group) => {
    const items = group.variables
      .map(
        (v) => `
          <button
            type="button"
            data-token="${v.token}"
            title="${v.description}"
            class="tvs-var-item"
          >
            <code>${v.token}</code>
            <span>${v.description}</span>
          </button>`,
      )
      .join("");
    return `
      <div class="tvs-var-group">
        <div class="tvs-var-group-label">${group.label}</div>
        <div class="tvs-var-grid">${items}</div>
      </div>`;
  }).join("");

  return `
    <style>
      .tvs-var-panel {
        font-family: Inter, ui-sans-serif, system-ui, sans-serif;
        background: #ffffff;
        color: #1f2937;
        padding: 20px 22px;
        border-radius: 10px;
        width: min(720px, 92vw);
        max-height: 70vh;
        overflow-y: auto;
        box-sizing: border-box;
      }
      .tvs-var-panel * { box-sizing: border-box; }
      .tvs-var-panel h3 {
        margin: 0 0 4px 0; color: #0C3688; font-size: 16px; font-weight: 600;
      }
      .tvs-var-panel p {
        margin: 0 0 18px 0; color: #475569; font-size: 13px; line-height: 1.5;
      }
      .tvs-var-group { margin-bottom: 18px; }
      .tvs-var-group:last-child { margin-bottom: 0; }
      .tvs-var-group-label {
        display: inline-block;
        font-size: 10px; text-transform: uppercase; letter-spacing: 1px;
        color: #ffffff; background: #0C3688;
        padding: 3px 10px; border-radius: 999px;
        font-weight: 700; margin-bottom: 10px;
      }
      .tvs-var-grid {
        display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px;
      }
      .tvs-var-item {
        display: flex; flex-direction: column; align-items: flex-start;
        gap: 4px; width: 100%;
        padding: 10px 12px;
        border: 1px solid #e2e8f0; border-radius: 8px;
        background: #f8fafc; cursor: pointer; text-align: left;
        transition: all .15s ease;
        font-family: inherit;
      }
      .tvs-var-item:hover {
        border-color: #0C3688; background: #ffffff;
        box-shadow: 0 2px 6px rgba(12, 54, 136, 0.08);
        transform: translateY(-1px);
      }
      .tvs-var-item code {
        font-family: ui-monospace, "SF Mono", Menlo, monospace;
        font-size: 13px; font-weight: 600; color: #0C3688;
        background: rgba(12, 54, 136, 0.08);
        padding: 2px 6px; border-radius: 4px;
        word-break: break-all;
      }
      .tvs-var-item span {
        font-size: 12px; color: #475569; line-height: 1.4;
      }
      @media (max-width: 560px) {
        .tvs-var-grid { grid-template-columns: 1fr; }
      }
    </style>
    <div class="tvs-var-panel">
      <h3>Variables disponibles</h3>
      <p>Haz click sobre un texto del email primero, después elige una variable para insertarla en el cursor.</p>
      ${groups}
    </div>`;
};

export const variablePickerPlugin = (editor: Editor): void => {
  editor.Commands.add(COMMAND_ID, {
    run(ed) {
      ed.Modal.open({
        title: "Insertar variable",
        content: buildPanelHtml(),
      });
      // Bind clicks once modal is rendered
      queueMicrotask(() => {
        const root = document.querySelector(".tvs-var-panel");
        if (!root) return;
        root.querySelectorAll<HTMLButtonElement>(".tvs-var-item").forEach((btn) => {
          btn.addEventListener("click", () => {
            const token = btn.dataset.token;
            if (!token) return;
            ed.Modal.close();
            insertToken(ed, token);
          });
        });
      });
    },
  });

  editor.Panels.addButton("options", {
    id: PANEL_ID,
    className: "fa fa-tag",
    label: "{{var}}",
    command: COMMAND_ID,
    attributes: { title: "Insertar variable Handlebars" },
  });
};

function insertToken(editor: Editor, token: string): void {
  const selected = editor.getSelected();
  if (!selected) {
    editor.Modal.open({
      title: "Selecciona un bloque",
      content: `<p style="font-family:Inter,sans-serif">Haz click sobre un texto del email primero, luego vuelve a abrir Variables.</p>`,
    });
    return;
  }

  // If a rich text editor is currently active, insert at caret using DOM Selection.
  const sel = selected.view?.el?.ownerDocument?.getSelection?.();
  if (sel && sel.rangeCount > 0 && !sel.isCollapsed === false) {
    const range = sel.getRangeAt(0);
    range.deleteContents();
    range.insertNode(document.createTextNode(token));
    range.collapse(false);
    return;
  }

  // Fallback: append to component content
  const current = selected.get("content") ?? "";
  selected.set("content", `${current}${token}`);
}
